// H5 / 浏览器端：偏大图片在上传前做轻度缩放与 JPEG 重编码，减小体积。
const JPEG_MIME = 'image/jpeg';

/** @returns {Promise<{ source: CanvasImageSource, cleanup: () => void }>} */
function loadDecodableImage(file) {
  return new Promise((resolve, reject) => {
    (async () => {
      try {
        if (typeof createImageBitmap === 'function') {
          const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
          resolve({
            source: bmp,
            cleanup: () => {
              try {
                bmp.close();
              } catch {
                /* ignore */
              }
            }
          });
          return;
        }
      } catch {
        /* fall through */
      }
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ source: img, cleanup: () => {} });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('decode'));
      };
      img.src = url;
    })();
  });
}

/**
 * @param {CanvasImageSource} source
 * @param {number} maxLongEdge
 */
function drawToCanvas(source, maxLongEdge) {
  const sw = 'width' in source ? source.width : 0;
  const sh = 'height' in source ? source.height : 0;
  let w = sw;
  let h = sh;
  const long = Math.max(w, h);
  if (long > maxLongEdge) {
    const scale = maxLongEdge / long;
    w = Math.round(w * scale);
    h = Math.round(h * scale);
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d');
  ctx.drawImage(source, 0, 0, w, h);
  return canvas;
}

/**
 * 偏大则自动轻度压缩；已较小或无法解码则返回原文件。
 * @param {File} file
 * @param {object} [options]
 * @param {number} [options.maxLongEdge=2048]
 * @param {number} [options.maxBytes] 目标最大体积（压缩后尽量不超过）
 * @param {number} [options.minQuality=0.7]
 * @param {number} [options.skipBelowBytes] 小于此体积不处理
 * @returns {Promise<File>}
 */
export async function compressImageForUpload(file, options = {}) {
  if (!file || !file.type?.startsWith('image/')) return file;
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') return file;

  const maxLongEdge = options.maxLongEdge ?? 2048;
  const maxBytes = options.maxBytes ?? 1.25 * 1024 * 1024;
  const minQuality = options.minQuality ?? 0.7;
  const skipBelowBytes = options.skipBelowBytes ?? 512 * 1024;

  if (file.size <= skipBelowBytes) return file;

  try {
    const { source, cleanup } = await loadDecodableImage(file);
    /** @type {HTMLCanvasElement} */
    let canvas;
    try {
      canvas = drawToCanvas(source, maxLongEdge);
    } finally {
      cleanup();
    }

    const toJpeg = (c, q) =>
      new Promise((resolve) => {
        c.toBlob((b) => resolve(b), JPEG_MIME, q);
      });

    let quality = 0.88;
    let blob = await toJpeg(canvas, quality);
    while (blob && blob.size > maxBytes && quality > minQuality) {
      quality -= 0.05;
      blob = await toJpeg(canvas, quality);
    }

    if (blob && blob.size > maxBytes && canvas.width > 960) {
      const scale = 0.82;
      const w2 = Math.max(480, Math.round(canvas.width * scale));
      const h2 = Math.max(480, Math.round(canvas.height * scale));
      const c2 = document.createElement('canvas');
      c2.width = w2;
      c2.height = h2;
      const ctx2 = c2.getContext('2d');
      if (ctx2) {
        ctx2.drawImage(canvas, 0, 0, w2, h2);
        quality = 0.85;
        blob = await toJpeg(c2, quality);
        while (blob && blob.size > maxBytes && quality > minQuality) {
          quality -= 0.05;
          blob = await toJpeg(c2, quality);
        }
      }
    }

    if (!blob || blob.size >= file.size) return file;

    const base = (file.name && file.name.replace(/\.[^.]+$/, '')) || 'photo';
    return new File([blob], `${base}.jpg`, { type: JPEG_MIME, lastModified: Date.now() });
  } catch {
    return file;
  }
}
